import type { TypedSupabaseClient } from '../client';
import { orContainsAnyColumn } from './postgrest-search';
import { mirrorStorefrontStockForArticles } from './stock-repository';
// Legacy bare-filename → full-URL resolver, shared with the article
// repository so every surface resolves images identically (./storage-image.ts).
// Aliased to the historical local name to keep the call sites below unchanged.
import { resolveStorageImageUrl as resolveStorageImage } from './storage-image';
import {
  joinAuthorizedArticleCategories,
  joinStorefrontCatalogArticleCategories,
} from './authorized-article-category-join';
import { fetchAllRows, withStableOrder } from './fetch-all-rows';
import type {
  IStorefrontCategoryRepository,
  IStorefrontProductRepository,
  IStoreSettingsRepository,
  IStoreThemeRepository,
  IStoreBannerRepository,
  IStorePageRepository,
  IStoreCheckoutFieldRepository,
  StorefrontCategory,
  CreateStorefrontCategoryInput,
  UpdateStorefrontCategoryInput,
  StorefrontCategoryFilters,
  StorefrontProduct,
  CreateStorefrontProductInput,
  UpdateStorefrontProductInput,
  StorefrontProductFilters,
  StoreSettings,
  UpdateStoreSettingsInput,
  NewsletterSubscriber,
  NewsletterSubscribeInput,
  NewsletterCampaign,
  CreateNewsletterCampaignInput,
  NewsletterTestSendInput,
  StoreTheme,
  UpdateStoreThemeInput,
  StoreBanner,
  CreateStoreBannerInput,
  UpdateStoreBannerInput,
  StorePage,
  UpdateStorePageInput,
  StoreCheckoutField,
  CreateStoreCheckoutFieldInput,
  UpdateStoreCheckoutFieldInput,
  CheckoutFieldOption,
  CheckoutFieldType,
  StoreBannerSlot,
  StorePageKey,
  TrustItem,
} from '../../../interfaces/src';

// ============================================================
// STOREFRONT CATEGORIES
// ============================================================

export class SupabaseStorefrontCategoryRepository implements IStorefrontCategoryRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(
    filters?: StorefrontCategoryFilters,
  ): Promise<StorefrontCategory[]> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('storefront_categories')
      .select('*');
    if (filters?.isPublished !== undefined)
      q = q.eq('is_published', filters.isPublished);
    // Chercher sur le seul `slug` ne trouvait pas une catégorie tapée telle
    // qu'elle s'affiche : « Éclairage » ne matche pas le slug « eclairage ».
    if (filters?.search)
      q = q.or(
        orContainsAnyColumn(
          ['slug', 'name_fr', 'name_ar', 'name_en'],
          filters.search,
        ),
      );
    const { data, error } = await q.order('display_order').order('created_at');
    if (error) throw error;
    const rows = await joinAuthorizedArticleCategories(
      this.supabase,
      (data ?? []) as Record<string, unknown>[],
    );
    return rows.map((row) => mapStorefrontCategory(row, this.supabase));
  }

  async listPublic(tenantId?: string): Promise<StorefrontCategory[]> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('storefront_categories')
      .select('*')
      .eq('is_published', true);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.order('display_order');
    if (error) throw error;
    const rows = await joinStorefrontCatalogArticleCategories(
      this.supabase,
      (data ?? []) as Record<string, unknown>[],
    );
    return rows.map((row) => mapStorefrontCategory(row, this.supabase));
  }

  async getById(id: string): Promise<StorefrontCategory | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_categories')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const [row] = await joinAuthorizedArticleCategories(this.supabase, [
      data as Record<string, unknown>,
    ]);
    return row ? mapStorefrontCategory(row, this.supabase) : null;
  }

  async getBySlug(
    slug: string,
    tenantId?: string,
  ): Promise<StorefrontCategory | null> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('storefront_categories')
      .select('*')
      .eq('slug', slug);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    if (!data) return null;
    const [row] = await joinStorefrontCatalogArticleCategories(this.supabase, [
      data as Record<string, unknown>,
    ]);
    return row ? mapStorefrontCategory(row, this.supabase) : null;
  }

  async create(
    input: CreateStorefrontCategoryInput,
  ): Promise<StorefrontCategory> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_categories')
      .insert({
        article_category_id: input.articleCategoryId,
        slug: input.slug,
        name_fr: input.nameFr ?? null,
        name_ar: input.nameAr ?? null,
        name_en: input.nameEn ?? null,
        banner_image_url: input.bannerImageUrl ?? null,
        display_order: input.displayOrder ?? 0,
        is_published: input.isPublished ?? true,
        ads_link_slug: input.adsLinkSlug ?? null,
      })
      .select('*')
      .single();
    if (error) throw error;
    const [row] = await joinAuthorizedArticleCategories(this.supabase, [
      data as Record<string, unknown>,
    ]);
    if (!row) throw new Error('storefront_category_readback_failed');
    return mapStorefrontCategory(row, this.supabase);
  }

  async update(
    id: string,
    input: UpdateStorefrontCategoryInput,
  ): Promise<StorefrontCategory> {
    const patch: Record<string, unknown> = {};
    if (input.slug !== undefined) patch['slug'] = input.slug;
    if (input.nameFr !== undefined) patch['name_fr'] = input.nameFr;
    if (input.nameAr !== undefined) patch['name_ar'] = input.nameAr;
    if (input.nameEn !== undefined) patch['name_en'] = input.nameEn;
    if (input.bannerImageUrl !== undefined)
      patch['banner_image_url'] = input.bannerImageUrl;
    if (input.displayOrder !== undefined)
      patch['display_order'] = input.displayOrder;
    if (input.isPublished !== undefined)
      patch['is_published'] = input.isPublished;
    if (input.adsLinkSlug !== undefined)
      patch['ads_link_slug'] = input.adsLinkSlug;

    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_categories')
      .update(patch)
      .eq('id', id)
      .select('*')
      .single();
    if (error) throw error;
    const [row] = await joinAuthorizedArticleCategories(this.supabase, [
      data as Record<string, unknown>,
    ]);
    if (!row) throw new Error('storefront_category_readback_failed');
    return mapStorefrontCategory(row, this.supabase);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_categories')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('storefront_categories')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapStorefrontCategory(
  row: Record<string, unknown>,
  supabase?: TypedSupabaseClient,
): StorefrontCategory {
  const joined = row['article_categories'] as {
    name?: string;
    image_url?: string | null;
    parent_id?: string | null;
  } | null;
  const tenantId = (row['tenant_id'] as string | null) ?? null;
  const resolve = (
    value: string | null | undefined,
    folder: string,
  ): string | null => {
    if (!supabase) return value && typeof value === 'string' ? value : null;
    return resolveStorageImage(supabase, value, { tenantId, folder });
  };
  return {
    id: row['id'] as string,
    articleCategoryId: row['article_category_id'] as string,
    parentArticleCategoryId: joined?.parent_id ?? null,
    slug: row['slug'] as string,
    nameFr: (row['name_fr'] as string) ?? null,
    nameAr: (row['name_ar'] as string) ?? null,
    nameEn: (row['name_en'] as string) ?? null,
    bannerImageUrl: resolve(
      row['banner_image_url'] as string | null,
      'categories',
    ),
    displayOrder: (row['display_order'] as number) ?? 0,
    isPublished: (row['is_published'] as boolean) ?? true,
    adsLinkSlug: (row['ads_link_slug'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    articleCategoryName: joined?.name,
    articleCategoryImageUrl: resolve(joined?.image_url ?? null, 'categories'),
  };
}

// ============================================================
// STOREFRONT PRODUCTS
// ============================================================

// Le hop `articles` de la boutique a DEUX projections, et le choix se fait sur
// l'appelant, pas sur la requête :
//
//   * back-office (erp-web / erp-mobile, toujours authentifié) -> la vue de
//     masquage `authorized_articles`, qui applique les politiques de colonnes
//     par rôle et la visibilité des coûts ;
//   * boutique publique (anon ET client en ligne) -> `storefront_catalog_articles`,
//     les 11 colonnes déjà publiques, restreintes aux articles d'un produit
//     PUBLIÉ. `authorized_articles` n'est pas grantée à anon et ne peut pas
//     l'être (cf. migration 20260826090000) : c'est tout l'objet de cette vue.
//
// Les deux aliasent la clé `articles`, donc `mapStorefrontProduct` ne change pas.
// ⚠ Le back-office doit voir les produits NON publiés : il ne peut donc pas
// passer par la vue publique, qui les filtre.
const ADMIN_PRODUCT_SELECT = `
        *,
        articles:authorized_articles(code, name, sale_price_ttc, vat_rate, image_url, images, category_id),
        storefront_product_categories(storefront_category_id)
      `;

const PUBLIC_PRODUCT_SELECT = `
        *,
        articles:storefront_catalog_articles(code, name, sale_price_ttc, vat_rate, image_url, images, category_id),
        storefront_product_categories(storefront_category_id)
      `;

export class SupabaseStorefrontProductRepository implements IStorefrontProductRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(filters?: StorefrontProductFilters): Promise<StorefrontProduct[]> {
    return this.queryProducts(ADMIN_PRODUCT_SELECT, filters);
  }

  private async queryProducts(
    select: string,
    filters?: StorefrontProductFilters,
  ): Promise<StorefrontProduct[]> {
    // An un-ranged PostgREST select stops silently at max-rows (1000). The
    // Articles publication filter and the back-office product catalogue need
    // the whole set, so walk stable pages with a fresh builder every time.
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) => {
      let q = (this.supabase as unknown as { from: (t: string) => any })
        .from('storefront_products')
        .select(select);

      if (filters?.isPublished !== undefined)
        q = q.eq('is_published', filters.isPublished);
      if (filters?.isFeatured !== undefined)
        q = q.eq('is_featured', filters.isFeatured);
      if (filters?.tenantId) q = q.eq('tenant_id', filters.tenantId);

      q = q
        .order('display_order')
        .order('created_at', { ascending: false });
      return withStableOrder(q).range(from, to);
    });
    let mapped: StorefrontProduct[] = rows.map(
      (row: Record<string, unknown>) =>
        mapStorefrontProduct(row, this.supabase),
    );
    if (filters?.categoryId) {
      const catId = filters.categoryId;
      mapped = mapped.filter((p: StorefrontProduct) =>
        p.categoryIds?.includes(catId),
      );
    }
    // La recherche se fait ici et non en base : une partie de ce qu'on cherche
    // (code article, nom de l'article) vit dans la table jointe, que PostgREST
    // ne sait pas mêler aux colonnes du parent dans un même `or`. Filtrer côté
    // serveur sur `slug` et `name_fr` seuls revenait à ne jamais trouver un
    // produit par sa référence — le premier réflexe quand on l'a sous les yeux
    // sur une facture. Même parti pris que `categoryId` juste au-dessus.
    if (filters?.search) {
      const needle = filters.search.trim().toLowerCase();
      if (needle) {
        mapped = mapped.filter((p) =>
          [
            p.slug,
            p.nameFr,
            p.nameAr,
            p.nameEn,
            p.reference,
            p.articleCode,
            p.articleName,
          ].some((v) => (v ?? '').toLowerCase().includes(needle)),
        );
      }
    }
    return mapped;
  }

  /** Chemin PUBLIC (anon + client en ligne) — voir PUBLIC_PRODUCT_SELECT.
   *  Ne délègue plus à `list()` : les deux ne lisent pas la même projection. */
  async listPublic(
    filters?: StorefrontProductFilters,
  ): Promise<StorefrontProduct[]> {
    return this.queryProducts(PUBLIC_PRODUCT_SELECT, {
      ...filters,
      isPublished: true,
    });
  }

  async getById(id: string): Promise<StorefrontProduct | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_products')
      .select(ADMIN_PRODUCT_SELECT)
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStorefrontProduct(data, this.supabase) : null;
  }

  async getBySlug(
    slug: string,
    tenantId?: string,
  ): Promise<StorefrontProduct | null> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('storefront_products')
      .select(PUBLIC_PRODUCT_SELECT)
      .eq('slug', slug);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    return data ? mapStorefrontProduct(data, this.supabase) : null;
  }

  /**
   * Chemin PUBLIC par id — la re-vérification de disponibilité du panier
   * (`cart-availability.ts`), qui tourne en `anon`. `getById` lit
   * `authorized_articles`, interdite à anon : la boutique ne peut donc pas
   * l'appeler. Le filtre « produit publié » de la vue publique va d'ailleurs
   * dans le sens de cet appelant, qui compte déjà un produit dépublié comme
   * indisponible.
   */
  async getPublicById(
    id: string,
    tenantId?: string,
  ): Promise<StorefrontProduct | null> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('storefront_products')
      .select(PUBLIC_PRODUCT_SELECT)
      .eq('id', id)
      .eq('is_published', true);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    return data ? mapStorefrontProduct(data, this.supabase) : null;
  }

  async getByArticleId(articleId: string): Promise<StorefrontProduct | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('authorized_storefront_products')
      .select('*')
      .eq('article_id', articleId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStorefrontProduct(data, this.supabase) : null;
  }

  async listHeroImageUrlsByArticleIds(
    articleIds: string[],
  ): Promise<Record<string, string>> {
    const unique = [...new Set(articleIds.filter(Boolean))];
    if (unique.length === 0) return {};
    const out: Record<string, string> = {};
    // `.in()` voyage dans la query string : découper comme ailleurs dans le repo.
    for (let i = 0; i < unique.length; i += 500) {
      const chunk = unique.slice(i, i + 500);
      const { data, error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('storefront_products')
        .select('article_id, hero_image_url, tenant_id')
        .in('article_id', chunk)
        .not('hero_image_url', 'is', null);
      if (error) throw error;
      for (const row of (data ?? []) as Array<Record<string, unknown>>) {
        const url = resolveStorageImage(
          this.supabase,
          row['hero_image_url'] as string | null,
          {
            tenantId: (row['tenant_id'] as string | null) ?? null,
            folder: 'hero',
          },
        );
        if (url) out[row['article_id'] as string] = url;
      }
    }
    return out;
  }

  async create(
    input: CreateStorefrontProductInput,
  ): Promise<StorefrontProduct> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_products')
      .insert({
        article_id: input.articleId,
        // The tenant-wide total is refreshed below without exposing another
        // store's movement rows to a restricted user. `null` means the article
        // has not started being tracked yet.
        stock_quantity: null,
        out_of_stock: input.outOfStock ?? false,
        slug: input.slug,
        name_fr: input.nameFr ?? null,
        name_ar: input.nameAr ?? null,
        name_en: input.nameEn ?? null,
        description_fr: input.descriptionFr ?? null,
        description_ar: input.descriptionAr ?? null,
        description_en: input.descriptionEn ?? null,
        reference: input.reference ?? null,
        hero_image_url: input.heroImageUrl ?? null,
        gallery_images: input.galleryImages ?? [],
        price_override: input.priceOverride ?? null,
        compare_at_price: input.compareAtPrice ?? null,
        seo_title_fr: input.seoTitleFr ?? null,
        seo_title_ar: input.seoTitleAr ?? null,
        seo_title_en: input.seoTitleEn ?? null,
        seo_description_fr: input.seoDescriptionFr ?? null,
        seo_description_ar: input.seoDescriptionAr ?? null,
        seo_description_en: input.seoDescriptionEn ?? null,
        ads_link_slug: input.adsLinkSlug ?? null,
        is_featured: input.isFeatured ?? false,
        is_published: input.isPublished ?? true,
        display_order: input.displayOrder ?? 0,
        published_at: new Date().toISOString(),
      })
      .select(ADMIN_PRODUCT_SELECT)
      .single();
    if (error) throw error;

    const product = mapStorefrontProduct(data, this.supabase);
    if (input.categoryIds && input.categoryIds.length > 0) {
      await this.setCategories(product.id, input.categoryIds);
    }
    await mirrorStorefrontStockForArticles(this.supabase, [input.articleId]);
    try {
      return (await this.getById(product.id)) ?? product;
    } catch {
      // Creation succeeded; a best-effort mirror reread must not turn that
      // successful write into a false failure for the caller.
      return product;
    }
  }

  async update(
    id: string,
    input: UpdateStorefrontProductInput,
  ): Promise<StorefrontProduct> {
    const patch: Record<string, unknown> = {};
    if (input.slug !== undefined) patch['slug'] = input.slug;
    if (input.nameFr !== undefined) patch['name_fr'] = input.nameFr;
    if (input.nameAr !== undefined) patch['name_ar'] = input.nameAr;
    if (input.nameEn !== undefined) patch['name_en'] = input.nameEn;
    if (input.descriptionFr !== undefined)
      patch['description_fr'] = input.descriptionFr;
    if (input.descriptionAr !== undefined)
      patch['description_ar'] = input.descriptionAr;
    if (input.descriptionEn !== undefined)
      patch['description_en'] = input.descriptionEn;
    if (input.reference !== undefined) patch['reference'] = input.reference;
    if (input.heroImageUrl !== undefined)
      patch['hero_image_url'] = input.heroImageUrl;
    if (input.galleryImages !== undefined)
      patch['gallery_images'] = input.galleryImages;
    if (input.priceOverride !== undefined)
      patch['price_override'] = input.priceOverride;
    if (input.compareAtPrice !== undefined)
      patch['compare_at_price'] = input.compareAtPrice;
    if (input.seoTitleFr !== undefined)
      patch['seo_title_fr'] = input.seoTitleFr;
    if (input.seoTitleAr !== undefined)
      patch['seo_title_ar'] = input.seoTitleAr;
    if (input.seoTitleEn !== undefined)
      patch['seo_title_en'] = input.seoTitleEn;
    if (input.seoDescriptionFr !== undefined)
      patch['seo_description_fr'] = input.seoDescriptionFr;
    if (input.seoDescriptionAr !== undefined)
      patch['seo_description_ar'] = input.seoDescriptionAr;
    if (input.seoDescriptionEn !== undefined)
      patch['seo_description_en'] = input.seoDescriptionEn;
    if (input.adsLinkSlug !== undefined)
      patch['ads_link_slug'] = input.adsLinkSlug;
    if (input.isFeatured !== undefined) patch['is_featured'] = input.isFeatured;
    if (input.isPublished !== undefined)
      patch['is_published'] = input.isPublished;
    if (input.displayOrder !== undefined)
      patch['display_order'] = input.displayOrder;
    if (input.outOfStock !== undefined)
      patch['out_of_stock'] = input.outOfStock;

    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_products')
      .update(patch)
      .eq('id', id)
      .select(ADMIN_PRODUCT_SELECT)
      .single();
    if (error) throw error;

    if (input.categoryIds !== undefined) {
      await this.setCategories(id, input.categoryIds);
    }
    return mapStorefrontProduct(data, this.supabase);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_products')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async setCategories(productId: string, categoryIds: string[]): Promise<void> {
    const { error: delErr } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_product_categories')
      .delete()
      .eq('storefront_product_id', productId);
    if (delErr) throw delErr;

    if (categoryIds.length === 0) return;
    const rows = categoryIds.map((cid, i) => ({
      storefront_product_id: productId,
      storefront_category_id: cid,
      display_order: i,
    }));
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('storefront_product_categories')
      .insert(rows);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('storefront_products')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapStorefrontProduct(
  row: Record<string, unknown>,
  supabase?: TypedSupabaseClient,
): StorefrontProduct {
  const article = (row['articles'] as Record<string, unknown>) ?? {};
  const joinedCategories =
    (row['storefront_product_categories'] as Array<{
      storefront_category_id: string;
    }>) ?? [];
  const tenantId = (row['tenant_id'] as string | null) ?? null;
  const resolve = (
    value: string | null | undefined,
    folder: string,
  ): string | null => {
    if (!supabase) return value && typeof value === 'string' ? value : null;
    return resolveStorageImage(supabase, value, { tenantId, folder });
  };
  const galleryRaw = (row['gallery_images'] as string[]) ?? [];
  return {
    id: row['id'] as string,
    articleId: row['article_id'] as string,
    slug: row['slug'] as string,
    nameFr: (row['name_fr'] as string) ?? null,
    nameAr: (row['name_ar'] as string) ?? null,
    nameEn: (row['name_en'] as string) ?? null,
    descriptionFr: (row['description_fr'] as string) ?? null,
    descriptionAr: (row['description_ar'] as string) ?? null,
    descriptionEn: (row['description_en'] as string) ?? null,
    reference: (row['reference'] as string) ?? null,
    heroImageUrl: resolve(row['hero_image_url'] as string | null, 'hero'),
    galleryImages: galleryRaw
      .map((u) => resolve(u, 'gallery'))
      .filter((u): u is string => typeof u === 'string' && u.length > 0),
    priceOverride:
      row['price_override'] != null ? Number(row['price_override']) : null,
    compareAtPrice:
      row['compare_at_price'] != null ? Number(row['compare_at_price']) : null,
    seoTitleFr: (row['seo_title_fr'] as string) ?? null,
    seoTitleAr: (row['seo_title_ar'] as string) ?? null,
    seoTitleEn: (row['seo_title_en'] as string) ?? null,
    seoDescriptionFr: (row['seo_description_fr'] as string) ?? null,
    seoDescriptionAr: (row['seo_description_ar'] as string) ?? null,
    seoDescriptionEn: (row['seo_description_en'] as string) ?? null,
    adsLinkSlug: (row['ads_link_slug'] as string) ?? null,
    isFeatured: (row['is_featured'] as boolean) ?? false,
    isPublished: (row['is_published'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 0,
    stockQuantity:
      row['stock_quantity'] != null ? Number(row['stock_quantity']) : null,
    outOfStock: (row['out_of_stock'] as boolean) ?? false,
    publishedAt: (row['published_at'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
    articleCode: article['code'] as string | undefined,
    articleName: article['name'] as string | undefined,
    articleCategoryId: (article['category_id'] as string | null) ?? null,
    articleSalePriceTtc:
      article['sale_price_ttc'] != null
        ? Number(article['sale_price_ttc'])
        : undefined,
    articleVatRate:
      article['vat_rate'] != null ? Number(article['vat_rate']) : undefined,
    // The article's image is the live default shown wherever a storefront
    // product has no hero of its own. The gallery `images[]` is the reliable
    // source (it points to a real uploaded file), so we prefer it; the legacy
    // single `image_url` is the fallback. Preferring `image_url` first would
    // let a dangling legacy filename (a slug whose file was never uploaded)
    // shadow a perfectly good gallery image and render the product blank.
    articleImageUrl:
      resolve(
        ((article['images'] as string[] | null) ?? []).find(
          (u) => typeof u === 'string' && u.trim().length > 0,
        ) ?? null,
        'articles',
      ) ?? resolve(article['image_url'] as string | null, 'articles'),
    categoryIds: joinedCategories.map((c) => c.storefront_category_id),
  };
}

// ============================================================
// STORE SETTINGS (singleton)
// ============================================================

export class SupabaseStoreSettingsRepository implements IStoreSettingsRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async get(): Promise<StoreSettings | null> {
    // Filter by current_user_tenant_id() so platform admins (whose RLS
    // exposes every tenant's row) read THIS tenant's settings, not a
    // random one. Same defence as company_settings.
    type SbAny = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId } = await (this.supabase as unknown as SbAny).rpc(
      'current_user_tenant_id',
      {},
    );
    if (!tenantId) return null;
    return this.getForTenant(tenantId);
  }

  /** Used by the storefront app, which has resolved the hostname to a
   *  tenant_id via tenants.getByHostname() and now wants the matching
   *  store_settings row. RLS lets anon read these. */
  async getForTenant(tenantId: string): Promise<StoreSettings | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_settings')
      .select('*')
      .eq('tenant_id', tenantId)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStoreSettings(data) : null;
  }

  /** Localhost/dev preview fallback: the first publicly-readable settings row.
   *  Anon RLS exposes published storefront settings, so this works without
   *  auth. Not used by production hostname routing. */
  async getAnyPublic(): Promise<StoreSettings | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_settings')
      .select('*')
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStoreSettings(data) : null;
  }

  async update(input: UpdateStoreSettingsInput): Promise<StoreSettings> {
    const existing = await this.get();
    const patch: Record<string, unknown> = mapStoreSettingsPatch(input);
    let result: StoreSettings;
    if (!existing) {
      // tenant_id is filled in by the BEFORE-INSERT trigger, no need
      // to pass it. The legacy `singleton_key` column was dropped by
      // migration 20260426120000 — passing it back now would fail.
      const { data, error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('store_settings')
        .insert(patch)
        .select()
        .single();
      if (error) throw error;
      result = mapStoreSettings(data);
    } else {
      const { data, error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('store_settings')
        .update(patch)
        .eq('id', existing.id)
        .select()
        .single();
      if (error) throw error;
      result = mapStoreSettings(data);
    }

    // Keep `tenants.subdomain` / `tenants.custom_domain` in sync with the
    // store-settings row. The storefront's hostname router queries the
    // `tenants` table (`tenants.getByHostname`), so if the admin edits
    // their subdomain here without propagating to `tenants` the
    // <new>.gesty.tn URL serves "Store not found" because no tenant
    // matches the hostname. Update only when these fields actually
    // appear in the input — silent no-op on unrelated patches.
    const tenantPatch: Record<string, string | null> = {};
    if (input.subdomain !== undefined) {
      const v = input.subdomain?.trim().toLowerCase() || null;
      tenantPatch['subdomain'] = v;
    }
    if (input.customDomain !== undefined) {
      const v = input.customDomain?.trim().toLowerCase() || null;
      tenantPatch['custom_domain'] = v;
    }
    if (Object.keys(tenantPatch).length > 0 && result.tenantId) {
      // RLS on tenants: owner / admin / platform-admin can UPDATE.
      // Errors here are surfaced — a routing mismatch is worse than a
      // failed save the user can retry.
      const { error: tErr } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('tenants')
        .update(tenantPatch)
        .eq('id', result.tenantId);
      if (tErr) throw tErr;
    }

    return result;
  }

  async getByHostname(
    match:
      | { kind: 'subdomain'; value: string }
      | { kind: 'custom'; value: string },
  ): Promise<StoreSettings | null> {
    const v = match.value.trim().toLowerCase();
    if (!v) return null;
    const column = match.kind === 'subdomain' ? 'subdomain' : 'custom_domain';
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_settings')
      .select('*')
      .eq(column, v)
      .limit(1)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStoreSettings(data) : null;
  }

  async isSubdomainTaken(
    subdomain: string,
    excludeOwnId?: string | null,
  ): Promise<boolean> {
    const v = subdomain.trim().toLowerCase();
    if (!v) return false;
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_settings')
      .select('id', { head: true, count: 'exact' })
      .eq('subdomain', v);
    if (excludeOwnId) q = q.neq('id', excludeOwnId);
    const { count, error } = await q;
    if (error) throw error;
    return (count ?? 0) > 0;
  }

  async isCustomDomainTaken(
    customDomain: string,
    excludeOwnId?: string | null,
  ): Promise<boolean> {
    const v = customDomain.trim().toLowerCase();
    if (!v) return false;
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_settings')
      .select('id', { head: true, count: 'exact' })
      .eq('custom_domain', v);
    if (excludeOwnId) q = q.neq('id', excludeOwnId);
    const { count, error } = await q;
    if (error) throw error;
    return (count ?? 0) > 0;
  }

  /** Anonymous submit through the locked-table RPC (see migration
   *  20260718150000). Returns alreadySubscribed=true when the RPC reports the
   *  email was on the list before. */
  async subscribeToNewsletter(
    input: NewsletterSubscribeInput,
  ): Promise<{ alreadySubscribed: boolean }> {
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: boolean | null; error: { message: string } | null }>;
    };
    const { data, error } = await (this.supabase as unknown as SbRpc).rpc(
      'newsletter_subscribe',
      {
        p_tenant_id: input.tenantId,
        p_email: input.email,
        p_locale: input.locale ?? null,
        p_source: input.source ?? 'storefront',
      },
    );
    if (error) throw error;
    return { alreadySubscribed: data === false };
  }

  async listNewsletterSubscribers(): Promise<NewsletterSubscriber[]> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('newsletter_subscribers')
      .select('id, email, locale, source, created_at')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map((row) => ({
      id: row['id'] as string,
      email: row['email'] as string,
      locale: (row['locale'] as string) ?? null,
      source: (row['source'] as string) ?? 'storefront',
      createdAt: row['created_at'] as string,
    }));
  }

  async deleteNewsletterSubscriber(id: string): Promise<void> {
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('newsletter_subscribers')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async listNewsletterCampaigns(): Promise<NewsletterCampaign[]> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('newsletter_campaigns')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return ((data ?? []) as Record<string, unknown>[]).map(
      mapNewsletterCampaign,
    );
  }

  /** Insert the history row (RLS stamps it to the caller's tenant via the
   *  WITH CHECK + explicit tenant id), then let the Edge Function fan out and
   *  finalize counts. If the invoke itself dies we mark the row failed so it
   *  never lingers as "sending". */
  async sendNewsletterCampaign(
    input: CreateNewsletterCampaignInput,
  ): Promise<NewsletterCampaign> {
    type SbRpc = {
      rpc: (
        fn: string,
        args: Record<string, unknown>,
      ) => Promise<{ data: string | null; error: { message: string } | null }>;
    };
    const { data: tenantId, error: tenantError } = await (
      this.supabase as unknown as SbRpc
    ).rpc('current_user_tenant_id', {});
    if (tenantError) throw tenantError;
    if (!tenantId) throw new Error('no_tenant');

    const { data: row, error: insertError } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('newsletter_campaigns')
      .insert({
        tenant_id: tenantId,
        subject: input.subject,
        body: input.body,
        cta_label: input.ctaLabel ?? null,
        cta_url: input.ctaUrl ?? null,
      })
      .select()
      .single();
    if (insertError) throw insertError;
    const campaignId = (row as Record<string, unknown>)['id'] as string;

    const { data: result, error: invokeError } =
      await this.supabase.functions.invoke('newsletter-campaign', {
        body: { campaignId },
      });
    if (invokeError) {
      await (this.supabase as unknown as { from: (t: string) => any })
        .from('newsletter_campaigns')
        .update({ status: 'failed' })
        .eq('id', campaignId);
      throw invokeError;
    }

    const { data: finalRow, error: readError } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('newsletter_campaigns')
      .select('*')
      .eq('id', campaignId)
      .single();
    if (readError) throw readError;
    void result;
    return mapNewsletterCampaign(finalRow as Record<string, unknown>);
  }

  async sendNewsletterTest(input: NewsletterTestSendInput): Promise<void> {
    const { error } = await this.supabase.functions.invoke(
      'newsletter-campaign',
      {
        body: {
          test: {
            to: input.to,
            subject: input.subject,
            body: input.body,
            ctaLabel: input.ctaLabel ?? null,
            ctaUrl: input.ctaUrl ?? null,
          },
        },
      },
    );
    if (error) throw error;
  }
}

function mapStoreSettings(row: Record<string, unknown>): StoreSettings {
  return {
    id: row['id'] as string,
    storeName: (row['store_name'] as string) ?? null,
    legalName: (row['legal_name'] as string) ?? null,
    storeTitleFr: (row['store_title_fr'] as string) ?? null,
    storeTitleAr: (row['store_title_ar'] as string) ?? null,
    storeTitleEn: (row['store_title_en'] as string) ?? null,
    phone: (row['phone'] as string) ?? null,
    email: (row['email'] as string) ?? null,
    businessAddress: (row['business_address'] as string) ?? null,
    seoDescriptionFr: (row['seo_description_fr'] as string) ?? null,
    seoDescriptionAr: (row['seo_description_ar'] as string) ?? null,
    seoDescriptionEn: (row['seo_description_en'] as string) ?? null,
    socialFacebook: (row['social_facebook'] as string) ?? null,
    socialInstagram: (row['social_instagram'] as string) ?? null,
    socialTiktok: (row['social_tiktok'] as string) ?? null,
    whatsappNumber: (row['whatsapp_number'] as string) ?? null,
    whatsappEnabled: (row['whatsapp_enabled'] as boolean) ?? true,
    whatsappAnimation:
      (row['whatsapp_animation'] as StoreSettings['whatsappAnimation']) ??
      'shake',
    whatsappPosition:
      (row['whatsapp_position'] as StoreSettings['whatsappPosition']) ??
      'right',
    languages: (row['languages'] as string[]) ?? ['fr', 'ar', 'en'],
    defaultLanguage: (row['default_language'] as string) ?? 'fr',
    currency: (row['currency'] as string) ?? 'TND',
    decimalPlaces: (row['decimal_places'] as number) ?? 3,
    currencyPosition:
      (row['currency_position'] as StoreSettings['currencyPosition']) ??
      'after',
    subdomain: (row['subdomain'] as string) ?? null,
    customDomain: (row['custom_domain'] as string) ?? null,
    customDomainLiveAt: (row['custom_domain_live_at'] as string) ?? null,
    cartCrossSellHeadingFr:
      (row['cart_cross_sell_heading_fr'] as string) ?? null,
    cartCrossSellHeadingAr:
      (row['cart_cross_sell_heading_ar'] as string) ?? null,
    cartCrossSellHeadingEn:
      (row['cart_cross_sell_heading_en'] as string) ?? null,
    contactFormEmail: (row['contact_form_email'] as string) ?? null,
    announcementBarTextFr: (row['announcement_bar_text_fr'] as string) ?? null,
    announcementBarTextAr: (row['announcement_bar_text_ar'] as string) ?? null,
    announcementBarTextEn: (row['announcement_bar_text_en'] as string) ?? null,
    announcementBarEnabled:
      (row['announcement_bar_enabled'] as boolean) ?? false,
    googleAnalyticsId: (row['google_analytics_id'] as string) ?? null,
    facebookPixelId: (row['facebook_pixel_id'] as string) ?? null,
    tiktokPixelId: (row['tiktok_pixel_id'] as string) ?? null,
    heroKickerFr: (row['hero_kicker_fr'] as string) ?? null,
    heroKickerAr: (row['hero_kicker_ar'] as string) ?? null,
    heroKickerEn: (row['hero_kicker_en'] as string) ?? null,
    featuredTitleFr: (row['featured_title_fr'] as string) ?? null,
    featuredTitleAr: (row['featured_title_ar'] as string) ?? null,
    featuredTitleEn: (row['featured_title_en'] as string) ?? null,
    featuredSubtitleFr: (row['featured_subtitle_fr'] as string) ?? null,
    featuredSubtitleAr: (row['featured_subtitle_ar'] as string) ?? null,
    featuredSubtitleEn: (row['featured_subtitle_en'] as string) ?? null,
    categoriesTitleFr: (row['categories_title_fr'] as string) ?? null,
    categoriesTitleAr: (row['categories_title_ar'] as string) ?? null,
    categoriesTitleEn: (row['categories_title_en'] as string) ?? null,
    categoriesSubtitleFr: (row['categories_subtitle_fr'] as string) ?? null,
    categoriesSubtitleAr: (row['categories_subtitle_ar'] as string) ?? null,
    categoriesSubtitleEn: (row['categories_subtitle_en'] as string) ?? null,
    newsletterTitleFr: (row['newsletter_title_fr'] as string) ?? null,
    newsletterTitleAr: (row['newsletter_title_ar'] as string) ?? null,
    newsletterTitleEn: (row['newsletter_title_en'] as string) ?? null,
    newsletterSubtitleFr: (row['newsletter_subtitle_fr'] as string) ?? null,
    newsletterSubtitleAr: (row['newsletter_subtitle_ar'] as string) ?? null,
    newsletterSubtitleEn: (row['newsletter_subtitle_en'] as string) ?? null,
    newsletterEnabled: (row['newsletter_enabled'] as boolean) ?? true,
    trustItems: ((row['trust_items'] as TrustItem[]) ?? []) as TrustItem[],
    trustEnabled: (row['trust_enabled'] as boolean) ?? true,
    footerTaglineFr: (row['footer_tagline_fr'] as string) ?? null,
    footerTaglineAr: (row['footer_tagline_ar'] as string) ?? null,
    footerTaglineEn: (row['footer_tagline_en'] as string) ?? null,
    footerCopyright: (row['footer_copyright'] as string) ?? null,
    footerPaymentMethods: (row['footer_payment_methods'] as string[]) ?? [
      'Cash',
      'Visa',
      'MC',
      'Edinar',
    ],
    notificationSoundOrderUrl:
      (row['notification_sound_order_url'] as string) ?? null,
    notificationSoundReminderUrl:
      (row['notification_sound_reminder_url'] as string) ?? null,
    deliveryFee: Number(row['delivery_fee'] ?? 0),
    freeShippingThreshold:
      row['free_shipping_threshold'] == null
        ? null
        : Number(row['free_shipping_threshold']),
    tenantId: (row['tenant_id'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapStoreSettingsPatch(
  input: UpdateStoreSettingsInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    heroKickerFr: 'hero_kicker_fr',
    heroKickerAr: 'hero_kicker_ar',
    heroKickerEn: 'hero_kicker_en',
    featuredTitleFr: 'featured_title_fr',
    featuredTitleAr: 'featured_title_ar',
    featuredTitleEn: 'featured_title_en',
    featuredSubtitleFr: 'featured_subtitle_fr',
    featuredSubtitleAr: 'featured_subtitle_ar',
    featuredSubtitleEn: 'featured_subtitle_en',
    categoriesTitleFr: 'categories_title_fr',
    categoriesTitleAr: 'categories_title_ar',
    categoriesTitleEn: 'categories_title_en',
    categoriesSubtitleFr: 'categories_subtitle_fr',
    categoriesSubtitleAr: 'categories_subtitle_ar',
    categoriesSubtitleEn: 'categories_subtitle_en',
    newsletterTitleFr: 'newsletter_title_fr',
    newsletterTitleAr: 'newsletter_title_ar',
    newsletterTitleEn: 'newsletter_title_en',
    newsletterSubtitleFr: 'newsletter_subtitle_fr',
    newsletterSubtitleAr: 'newsletter_subtitle_ar',
    newsletterSubtitleEn: 'newsletter_subtitle_en',
    newsletterEnabled: 'newsletter_enabled',
    trustItems: 'trust_items',
    trustEnabled: 'trust_enabled',
    footerTaglineFr: 'footer_tagline_fr',
    footerTaglineAr: 'footer_tagline_ar',
    footerTaglineEn: 'footer_tagline_en',
    footerCopyright: 'footer_copyright',
    footerPaymentMethods: 'footer_payment_methods',
    storeName: 'store_name',
    legalName: 'legal_name',
    storeTitleFr: 'store_title_fr',
    storeTitleAr: 'store_title_ar',
    storeTitleEn: 'store_title_en',
    phone: 'phone',
    email: 'email',
    businessAddress: 'business_address',
    seoDescriptionFr: 'seo_description_fr',
    seoDescriptionAr: 'seo_description_ar',
    seoDescriptionEn: 'seo_description_en',
    socialFacebook: 'social_facebook',
    socialInstagram: 'social_instagram',
    socialTiktok: 'social_tiktok',
    whatsappNumber: 'whatsapp_number',
    whatsappEnabled: 'whatsapp_enabled',
    whatsappAnimation: 'whatsapp_animation',
    whatsappPosition: 'whatsapp_position',
    languages: 'languages',
    defaultLanguage: 'default_language',
    currency: 'currency',
    decimalPlaces: 'decimal_places',
    currencyPosition: 'currency_position',
    subdomain: 'subdomain',
    customDomain: 'custom_domain',
    cartCrossSellHeadingFr: 'cart_cross_sell_heading_fr',
    cartCrossSellHeadingAr: 'cart_cross_sell_heading_ar',
    cartCrossSellHeadingEn: 'cart_cross_sell_heading_en',
    contactFormEmail: 'contact_form_email',
    announcementBarTextFr: 'announcement_bar_text_fr',
    announcementBarTextAr: 'announcement_bar_text_ar',
    announcementBarTextEn: 'announcement_bar_text_en',
    announcementBarEnabled: 'announcement_bar_enabled',
    googleAnalyticsId: 'google_analytics_id',
    facebookPixelId: 'facebook_pixel_id',
    tiktokPixelId: 'tiktok_pixel_id',
    notificationSoundOrderUrl: 'notification_sound_order_url',
    notificationSoundReminderUrl: 'notification_sound_reminder_url',
    deliveryFee: 'delivery_fee',
    freeShippingThreshold: 'free_shipping_threshold',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

// ============================================================
// STORE THEME (singleton)
// ============================================================

export class SupabaseStoreThemeRepository implements IStoreThemeRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async get(tenantIdArg?: string): Promise<StoreTheme | null> {
    // Anonymous storefront callers pass the resolved tenantId directly
    // (no auth session → current_user_tenant_id() returns NULL). Admin
    // callers omit it and we resolve via RPC so platform admins (whose
    // RLS exposes every tenant) read THIS tenant's theme — and avoid
    // PGRST116 "result contains 2 rows" on `.maybeSingle`.
    let tenantId = tenantIdArg;
    if (!tenantId) {
      type SbAny = {
        rpc: (
          fn: string,
          args: Record<string, unknown>,
        ) => Promise<{
          data: string | null;
          error: { message: string } | null;
        }>;
      };
      const { data } = await (this.supabase as unknown as SbAny).rpc(
        'current_user_tenant_id',
        {},
      );
      tenantId = data ?? undefined;
    }
    if (!tenantId) return null;
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_theme')
      .select('*')
      .eq('tenant_id', tenantId)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStoreTheme(data) : null;
  }

  async update(input: UpdateStoreThemeInput): Promise<StoreTheme> {
    const existing = await this.get();
    const patch: Record<string, unknown> = mapStoreThemePatch(input);
    if (!existing) {
      // tenant_id auto-filled by trigger; singleton_key column was
      // dropped by migration 20260426120000.
      const { data, error } = await this.writeStoreThemeWithSchemaFallback(
        'insert',
        patch,
      );
      if (error) throw normalizeStoreThemeWriteError(error);
      return mapStoreTheme(data);
    }
    const { data, error } = await this.writeStoreThemeWithSchemaFallback(
      'update',
      patch,
      existing.id,
    );
    if (error) throw normalizeStoreThemeWriteError(error);
    return mapStoreTheme(data);
  }

  private async writeStoreThemeWithSchemaFallback(
    mode: 'insert' | 'update',
    patch: Record<string, unknown>,
    id?: string,
  ): Promise<{ data: Record<string, unknown>; error: unknown | null }> {
    let nextPatch = patch;
    for (let attempt = 0; attempt < 6; attempt++) {
      const query = (
        this.supabase as unknown as { from: (t: string) => any }
      ).from('store_theme');
      const result =
        mode === 'insert'
          ? await query.insert(nextPatch).select().single()
          : await query.update(nextPatch).eq('id', id).select().single();
      if (!result.error) return result;
      const recovered = recoverStoreThemePatchForMissingColumn(
        nextPatch,
        result.error,
      );
      if (!recovered) return result;
      nextPatch = recovered;
    }
    return {
      data: null as unknown as Record<string, unknown>,
      error: new Error('store_theme_schema_recovery_failed'),
    };
  }
}

function mapStoreTheme(row: Record<string, unknown>): StoreTheme {
  return {
    id: row['id'] as string,
    themeName: (row['theme_name'] as string) ?? 'woodmart',
    logoSquareUrl: (row['logo_square_url'] as string) ?? null,
    logoHorizontalUrl: (row['logo_horizontal_url'] as string) ?? null,
    loginLogoUrl: (row['login_logo_url'] as string) ?? null,
    faviconUrl: (row['favicon_url'] as string) ?? null,
    proLoginLogoUrl: (row['pro_login_logo_url'] as string) ?? null,
    proFaviconUrl: (row['pro_favicon_url'] as string) ?? null,
    primaryColor: (row['primary_color'] as string) ?? '#1B2A4A',
    secondaryColor: (row['secondary_color'] as string) ?? '#F5A623',
    tertiaryColor: (row['tertiary_color'] as string) ?? '#50E3C2',
    backgroundColor: (row['background_color'] as string) ?? '#FFFFFF',
    newsletterBackgroundColor:
      (row['newsletter_background_color'] as string) ?? null,
    footerBackgroundColor: (row['footer_background_color'] as string) ?? null,
    newsletterTextColor: (row['newsletter_text_color'] as string) ?? null,
    footerTextColor: (row['footer_text_color'] as string) ?? null,
    textColor: (row['text_color'] as string) ?? '#1A1A1A',
    saleColor: (row['sale_color'] as string) ?? null,
    ctaTextFr: (row['cta_text_fr'] as string) ?? null,
    ctaTextAr: (row['cta_text_ar'] as string) ?? null,
    ctaTextEn: (row['cta_text_en'] as string) ?? null,
    ctaTextColor: (row['cta_text_color'] as string) ?? '#FFFFFF',
    ctaButtonColor: (row['cta_button_color'] as string) ?? '#F5A623',
    ctaSticky: (row['cta_sticky'] as boolean) ?? true,
    ctaShake: (row['cta_shake'] as boolean) ?? false,
    stickyHeader: (row['sticky_header'] as boolean) ?? true,
    showSearchBar: (row['show_search_bar'] as boolean) ?? true,
    showCartBadge: (row['show_cart_badge'] as boolean) ?? true,
    showLanguageSwitcher: (row['show_language_switcher'] as boolean) ?? true,
    fontFamily: (row['font_family'] as string) ?? 'Inter',
    fontFamilyAr: (row['font_family_ar'] as string) ?? null,
    headingScale: (row['heading_scale'] as string) ?? 'default',
    borderRadius: (row['border_radius'] as string) ?? '0.5rem',
    shadowStyle: (row['shadow_style'] as string) ?? 'default',
    homepageSections:
      (row['homepage_sections'] as StoreTheme['homepageSections']) ?? null,
    pebbleSettings:
      (row['pebble_settings'] as Record<string, unknown> | null) ?? null,
    elessiSettings:
      (row['elessi_settings'] as Record<string, unknown> | null) ?? null,
    woodmartSettings:
      (row['woodmart_settings'] as Record<string, unknown> | null) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

function mapNewsletterCampaign(
  row: Record<string, unknown>,
): NewsletterCampaign {
  return {
    id: row['id'] as string,
    subject: (row['subject'] as string) ?? '',
    body: (row['body'] as string) ?? '',
    ctaLabel: (row['cta_label'] as string) ?? null,
    ctaUrl: (row['cta_url'] as string) ?? null,
    status: ((row['status'] as string) ??
      'sending') as NewsletterCampaign['status'],
    recipientsCount: Number(row['recipients_count'] ?? 0),
    sentCount: Number(row['sent_count'] ?? 0),
    failedCount: Number(row['failed_count'] ?? 0),
    createdAt: row['created_at'] as string,
    sentAt: (row['sent_at'] as string) ?? null,
  };
}

function mapStoreThemePatch(
  input: UpdateStoreThemeInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    themeName: 'theme_name',
    homepageSections: 'homepage_sections',
    pebbleSettings: 'pebble_settings',
    elessiSettings: 'elessi_settings',
    woodmartSettings: 'woodmart_settings',
    logoSquareUrl: 'logo_square_url',
    logoHorizontalUrl: 'logo_horizontal_url',
    loginLogoUrl: 'login_logo_url',
    faviconUrl: 'favicon_url',
    proLoginLogoUrl: 'pro_login_logo_url',
    proFaviconUrl: 'pro_favicon_url',
    primaryColor: 'primary_color',
    secondaryColor: 'secondary_color',
    tertiaryColor: 'tertiary_color',
    backgroundColor: 'background_color',
    newsletterBackgroundColor: 'newsletter_background_color',
    footerBackgroundColor: 'footer_background_color',
    newsletterTextColor: 'newsletter_text_color',
    footerTextColor: 'footer_text_color',
    textColor: 'text_color',
    saleColor: 'sale_color',
    ctaTextFr: 'cta_text_fr',
    ctaTextAr: 'cta_text_ar',
    ctaTextEn: 'cta_text_en',
    ctaTextColor: 'cta_text_color',
    ctaButtonColor: 'cta_button_color',
    ctaSticky: 'cta_sticky',
    ctaShake: 'cta_shake',
    stickyHeader: 'sticky_header',
    showSearchBar: 'show_search_bar',
    showCartBadge: 'show_cart_badge',
    showLanguageSwitcher: 'show_language_switcher',
    fontFamily: 'font_family',
    fontFamilyAr: 'font_family_ar',
    headingScale: 'heading_scale',
    borderRadius: 'border_radius',
    shadowStyle: 'shadow_style',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

function recoverStoreThemePatchForMissingColumn(
  patch: Record<string, unknown>,
  error: unknown,
): Record<string, unknown> | null {
  const missingColumn = extractMissingColumn(error);
  if (!missingColumn || !(missingColumn in patch)) return null;
  const next = { ...patch };
  if (
    missingColumn === 'login_logo_url' &&
    next['logo_horizontal_url'] === undefined
  ) {
    next['logo_horizontal_url'] = next[missingColumn];
  }
  if (
    missingColumn === 'pro_login_logo_url' &&
    next['logo_horizontal_url'] === undefined
  ) {
    next['logo_horizontal_url'] = next[missingColumn];
  }
  if (
    missingColumn === 'pro_favicon_url' &&
    next['favicon_url'] === undefined
  ) {
    next['favicon_url'] = next[missingColumn];
  }
  delete next[missingColumn];
  return Object.keys(next).length > 0 ? next : null;
}

function extractMissingColumn(error: unknown): string | null {
  const source =
    error && typeof error === 'object'
      ? `${'code' in error ? String(error.code) : ''} ${'message' in error ? String(error.message) : ''} ${'details' in error ? String(error.details) : ''}`
      : String(error ?? '');
  const match =
    source.match(/'([^']+)'\s+column/i) ??
    source.match(
      /column\s+"?([a-z0-9_]+)"?\s+(?:of relation|does not exist|not found)/i,
    );
  return match?.[1] ?? null;
}

function normalizeStoreThemeWriteError(error: unknown): unknown {
  const missingColumn = extractMissingColumn(error);
  if (!missingColumn) return error;
  if (
    missingColumn === 'newsletter_background_color' ||
    missingColumn === 'footer_background_color'
  ) {
    return new Error(
      'The storefront theme database migration is missing. Apply 20260512153000_store_theme_section_backgrounds.sql to save footer/newsletter colors.',
    );
  }
  if (
    missingColumn === 'newsletter_text_color' ||
    missingColumn === 'footer_text_color'
  ) {
    return new Error(
      'The storefront theme database migration is missing. Apply 20260718170000_footer_newsletter_text_colors.sql to save footer/newsletter text colors.',
    );
  }
  return error;
}

// ============================================================
// STORE BANNERS
// ============================================================

export class SupabaseStoreBannerRepository implements IStoreBannerRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(tenantId?: string): Promise<StoreBanner[]> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_banners')
      .select('*');
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.order('slot').order('display_order');
    if (error) throw error;
    return (data ?? []).map(mapStoreBanner);
  }

  async getById(id: string): Promise<StoreBanner | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_banners')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStoreBanner(data) : null;
  }

  async create(input: CreateStoreBannerInput): Promise<StoreBanner> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_banners')
      .insert(mapStoreBannerInsert(input))
      .select()
      .single();
    if (error) throw error;
    return mapStoreBanner(data);
  }

  async update(
    id: string,
    input: UpdateStoreBannerInput,
  ): Promise<StoreBanner> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_banners')
      .update(mapStoreBannerInsert(input))
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapStoreBanner(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_banners')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('store_banners')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapStoreBannerInsert(
  input: UpdateStoreBannerInput | CreateStoreBannerInput,
): Record<string, unknown> {
  const map: Record<string, string> = {
    slot: 'slot',
    displayOrder: 'display_order',
    desktopImageUrl: 'desktop_image_url',
    mobileImageUrl: 'mobile_image_url',
    titleFr: 'title_fr',
    titleAr: 'title_ar',
    titleEn: 'title_en',
    descriptionFr: 'description_fr',
    descriptionAr: 'description_ar',
    descriptionEn: 'description_en',
    ctaTextFr: 'cta_text_fr',
    ctaTextAr: 'cta_text_ar',
    ctaTextEn: 'cta_text_en',
    ctaLink: 'cta_link',
    ctaPosition: 'cta_position',
    textAlignment: 'text_alignment',
    overlayOpacity: 'overlay_opacity',
    isVisible: 'is_visible',
  };
  const patch: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined) continue;
    const col = map[k];
    if (col) patch[col] = v;
  }
  return patch;
}

function mapStoreBanner(row: Record<string, unknown>): StoreBanner {
  return {
    id: row['id'] as string,
    slot: row['slot'] as StoreBannerSlot,
    displayOrder: (row['display_order'] as number) ?? 0,
    desktopImageUrl: (row['desktop_image_url'] as string) ?? null,
    mobileImageUrl: (row['mobile_image_url'] as string) ?? null,
    titleFr: (row['title_fr'] as string) ?? null,
    titleAr: (row['title_ar'] as string) ?? null,
    titleEn: (row['title_en'] as string) ?? null,
    descriptionFr: (row['description_fr'] as string) ?? null,
    descriptionAr: (row['description_ar'] as string) ?? null,
    descriptionEn: (row['description_en'] as string) ?? null,
    ctaTextFr: (row['cta_text_fr'] as string) ?? null,
    ctaTextAr: (row['cta_text_ar'] as string) ?? null,
    ctaTextEn: (row['cta_text_en'] as string) ?? null,
    ctaLink: (row['cta_link'] as string) ?? null,
    ctaPosition: (row['cta_position'] as StoreBanner['ctaPosition']) ?? 'left',
    textAlignment:
      (row['text_alignment'] as StoreBanner['textAlignment']) ?? 'left',
    overlayOpacity: Number(row['overlay_opacity'] ?? 0.3),
    isVisible: (row['is_visible'] as boolean) ?? true,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

// ============================================================
// STORE PAGES
// ============================================================

export class SupabaseStorePageRepository implements IStorePageRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(tenantId?: string): Promise<StorePage[]> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_pages')
      .select('*');
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.order('display_order');
    if (error) throw error;
    return (data ?? []).map(mapStorePage);
  }

  async getById(id: string): Promise<StorePage | null> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_pages')
      .select('*')
      .eq('id', id)
      .maybeSingle();
    if (error) throw error;
    return data ? mapStorePage(data) : null;
  }

  async getBySlug(slug: string, tenantId?: string): Promise<StorePage | null> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_pages')
      .select('*')
      .eq('slug', slug);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    return data ? mapStorePage(data) : null;
  }

  async getByKey(key: string, tenantId?: string): Promise<StorePage | null> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_pages')
      .select('*')
      .eq('page_key', key);
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.maybeSingle();
    if (error) throw error;
    return data ? mapStorePage(data) : null;
  }

  async update(id: string, input: UpdateStorePageInput): Promise<StorePage> {
    const map: Record<string, string> = {
      slug: 'slug',
      titleFr: 'title_fr',
      titleAr: 'title_ar',
      titleEn: 'title_en',
      contentFr: 'content_fr',
      contentAr: 'content_ar',
      contentEn: 'content_en',
      seoTitleFr: 'seo_title_fr',
      seoTitleAr: 'seo_title_ar',
      seoTitleEn: 'seo_title_en',
      seoDescriptionFr: 'seo_description_fr',
      seoDescriptionAr: 'seo_description_ar',
      seoDescriptionEn: 'seo_description_en',
      isPublished: 'is_published',
      showInFooter: 'show_in_footer',
      displayOrder: 'display_order',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = v;
    }
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_pages')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapStorePage(data);
  }
}

function mapStorePage(row: Record<string, unknown>): StorePage {
  return {
    id: row['id'] as string,
    pageKey: row['page_key'] as StorePageKey,
    slug: row['slug'] as string,
    titleFr: (row['title_fr'] as string) ?? null,
    titleAr: (row['title_ar'] as string) ?? null,
    titleEn: (row['title_en'] as string) ?? null,
    contentFr: (row['content_fr'] as string) ?? null,
    contentAr: (row['content_ar'] as string) ?? null,
    contentEn: (row['content_en'] as string) ?? null,
    seoTitleFr: (row['seo_title_fr'] as string) ?? null,
    seoTitleAr: (row['seo_title_ar'] as string) ?? null,
    seoTitleEn: (row['seo_title_en'] as string) ?? null,
    seoDescriptionFr: (row['seo_description_fr'] as string) ?? null,
    seoDescriptionAr: (row['seo_description_ar'] as string) ?? null,
    seoDescriptionEn: (row['seo_description_en'] as string) ?? null,
    isPublished: (row['is_published'] as boolean) ?? true,
    showInFooter: (row['show_in_footer'] as boolean) ?? true,
    displayOrder: (row['display_order'] as number) ?? 0,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}

// ============================================================
// STORE CHECKOUT FIELDS
// ============================================================

export class SupabaseStoreCheckoutFieldRepository implements IStoreCheckoutFieldRepository {
  constructor(private supabase: TypedSupabaseClient) {}

  async list(tenantId?: string): Promise<StoreCheckoutField[]> {
    let q = (this.supabase as unknown as { from: (t: string) => any })
      .from('store_checkout_fields')
      .select('*');
    if (tenantId) q = q.eq('tenant_id', tenantId);
    const { data, error } = await q.order('display_order');
    if (error) throw error;
    return (data ?? []).map(mapCheckoutField);
  }

  async create(
    input: CreateStoreCheckoutFieldInput,
  ): Promise<StoreCheckoutField> {
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_checkout_fields')
      .insert({
        field_key: input.fieldKey,
        field_type: input.fieldType,
        label_fr: input.labelFr,
        label_ar: input.labelAr ?? null,
        label_en: input.labelEn ?? null,
        placeholder_fr: input.placeholderFr ?? null,
        placeholder_ar: input.placeholderAr ?? null,
        placeholder_en: input.placeholderEn ?? null,
        options: input.options ?? null,
        is_required: input.isRequired ?? false,
        is_visible: input.isVisible ?? true,
        display_order: input.displayOrder ?? 0,
        maps_to: input.mapsTo ?? null,
      })
      .select()
      .single();
    if (error) throw error;
    return mapCheckoutField(data);
  }

  async update(
    id: string,
    input: UpdateStoreCheckoutFieldInput,
  ): Promise<StoreCheckoutField> {
    const map: Record<string, string> = {
      fieldType: 'field_type',
      labelFr: 'label_fr',
      labelAr: 'label_ar',
      labelEn: 'label_en',
      placeholderFr: 'placeholder_fr',
      placeholderAr: 'placeholder_ar',
      placeholderEn: 'placeholder_en',
      options: 'options',
      isRequired: 'is_required',
      isVisible: 'is_visible',
      displayOrder: 'display_order',
      mapsTo: 'maps_to',
    };
    const patch: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(input)) {
      if (v === undefined) continue;
      const col = map[k];
      if (col) patch[col] = v;
    }
    const { data, error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_checkout_fields')
      .update(patch)
      .eq('id', id)
      .select()
      .single();
    if (error) throw error;
    return mapCheckoutField(data);
  }

  async delete(id: string): Promise<void> {
    const { error } = await (
      this.supabase as unknown as { from: (t: string) => any }
    )
      .from('store_checkout_fields')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async reorder(ids: string[]): Promise<void> {
    for (let i = 0; i < ids.length; i++) {
      const { error } = await (
        this.supabase as unknown as { from: (t: string) => any }
      )
        .from('store_checkout_fields')
        .update({ display_order: i })
        .eq('id', ids[i]);
      if (error) throw error;
    }
  }
}

function mapCheckoutField(row: Record<string, unknown>): StoreCheckoutField {
  return {
    id: row['id'] as string,
    fieldKey: row['field_key'] as string,
    fieldType: row['field_type'] as CheckoutFieldType,
    labelFr: (row['label_fr'] as string) ?? '',
    labelAr: (row['label_ar'] as string) ?? null,
    labelEn: (row['label_en'] as string) ?? null,
    placeholderFr: (row['placeholder_fr'] as string) ?? null,
    placeholderAr: (row['placeholder_ar'] as string) ?? null,
    placeholderEn: (row['placeholder_en'] as string) ?? null,
    options: (row['options'] as CheckoutFieldOption[] | null) ?? null,
    isRequired: (row['is_required'] as boolean) ?? false,
    isVisible: (row['is_visible'] as boolean) ?? true,
    isDefault: (row['is_default'] as boolean) ?? false,
    displayOrder: (row['display_order'] as number) ?? 0,
    mapsTo: (row['maps_to'] as string) ?? null,
    createdAt: row['created_at'] as string,
    updatedAt: row['updated_at'] as string,
  };
}
